import { useState } from "react";
import { X, UserPlus, CheckCircle2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const NAME_REGEX = /^[A-Za-z ]*$/;
const ORG_REGEX = /^[A-Za-z\-&'. ]*$/;

export default function WaitlistModal({ onClose }) {
  const { waitlist } = useAuth();

  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    org_name: "",
  });

  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};

    if (!form.full_name.trim())
      e.full_name = "Full name is required.";

    if (!NAME_REGEX.test(form.full_name))
      e.full_name = "Only letters and spaces allowed.";

    if (!form.org_name.trim())
      e.org_name = "Organisation name is required.";

    if (!ORG_REGEX.test(form.org_name))
      e.org_name =
        "Organisation name contains invalid characters.";

    if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)
    )
      e.email = "Enter a valid email.";

    setErrors(e);

    return Object.keys(e).length === 0;
  };

  const update = (key) => (e) =>
    setForm({
      ...form,
      [key]: e.target.value,
    });

  const submit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setBusy(true);

    try {
      await waitlist(form);

      setSuccess(true);

      toast.success("Successfully joined the waitlist");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Unable to join waitlist."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        background: "rgba(15,31,61,.45)",
        backdropFilter: "blur(4px)",
      }}
      onClick={(e) =>
        e.target === e.currentTarget && onClose()
      }
    >
      <div className="relative w-full max-w-md rounded-2xl bg-white border border-[#E2E8F0] shadow-2xl p-8">

        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
        >
          <X size={20} />
        </button>

        {!success ? (
          <>
            <div className="h-11 w-11 rounded-xl bg-[#EFF6FF] flex items-center justify-center mb-5">
              <UserPlus className="h-5 w-5 text-[#0F1F3D]" />
            </div>

            <h2 className="font-display text-2xl font-bold text-[#0F1F3D]">
              Join the Waitlist
            </h2>

            <p className="text-sm text-[#475569] mt-2">
              Be the first to know when LaunchRadar opens to new teams.
            </p>

            <form
              onSubmit={submit}
              className="space-y-4 mt-6"
            >
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B]">
                  Full name
                </label>

                <input
                  value={form.full_name}
                  onChange={update("full_name")}
                  className="mt-2 w-full rounded-md border border-[#E2E8F0] px-4 py-2.5"
                />

                {errors.full_name && (
                  <p className="text-xs text-red-500 mt-1">
                    {errors.full_name}
                  </p>
                )}
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B]">
                  Work email
                </label>

                <input
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  className="mt-2 w-full rounded-md border border-[#E2E8F0] px-4 py-2.5"
                />

                {errors.email && (
                  <p className="text-xs text-red-500 mt-1">
                    {errors.email}
                  </p>
                )}
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B]">
                  Organisation
                </label>

                <input
                  value={form.org_name}
                  onChange={update("org_name")}
                  className="mt-2 w-full rounded-md border border-[#E2E8F0] px-4 py-2.5"
                />

                {errors.org_name && (
                  <p className="text-xs text-red-500 mt-1">
                    {errors.org_name}
                  </p>
                )}
              </div>

              <button
                disabled={busy}
                className="w-full rounded-md bg-[#0F1F3D] py-2.5 text-white font-medium hover:bg-[#172A4F]"
              >
                {busy ? "Joining..." : "Join Waitlist"}
              </button>

              <button
                type="button"
                onClick={onClose}
                className="w-full rounded-md border border-[#E2E8F0] py-2.5"
              >
                Cancel
              </button>
            </form>
          </>
        ) : (
          <div className="text-center py-4">
            <div className="h-14 w-14 rounded-full bg-[#ECFDF5] flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="h-7 w-7 text-[#10B981]" />
            </div>

            <h2 className="text-2xl font-bold text-[#0F1F3D]">
              You're on the list!
            </h2>

            <p className="mt-3 text-sm text-[#475569]">
              Thanks for joining the LaunchRadar waitlist.
              We'll notify you as soon as access becomes
              available.
            </p>

            <button
              onClick={onClose}
              className="mt-6 rounded-md bg-[#0F1F3D] px-6 py-2.5 text-white"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}